import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import { MENU_MOBILE_BREAKPOINT } from 'modules/menu/constants';

type Props = {
  checked: boolean;
  name: string;
  value: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  icon?: React.ReactNode;
  labelId?: string;
};

type RootProps = {
  checked?: boolean;
};

type LabelProps = {
  checked?: boolean;
};

const Root = styled.div<RootProps>`
  border: 2px solid ${cbSemanticCssVars.borderPrimary};
  ${props => props.checked && `border: 2px solid ${colors.wolt100};`}
  border-radius: 8px;
  ${props => props.checked && `background: ${cbSemanticCssVars.surfaceMain};`}
  height: 160px;
  width: 100%;

  @media (min-width: ${MENU_MOBILE_BREAKPOINT}px) {
    flex: 1;
    max-width: 320px;
  }

  /* Since we're hiding the actual radio control, we use focus-within
     to indicate focus to the user with the border and colour.
     Focus indicator design needs to be reviewed later: this is insufficient
     for good keyboard UX
  */
  &:focus-within {
    background-color: ${cssThemedValue({ light: colors.pepper4, dark: colors.pepper32 })};
    ${props => props.checked && `background-color: ${colors.wolt12};`}
    color: ${colors.wolt100};
  }

  &:hover {
    background-color: ${cssThemedValue({ light: colors.pepper4, dark: colors.pepper32 })};
    ${props => props.checked && `background-color: ${colors.wolt12};`}
  }
`;

const Label = styled.label<LabelProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${cbSemanticCssVars.textSecondary};
  ${props => props.checked && `color: ${colors.wolt100};`}
  width: 100%;
  height: 100%;
  justify-content: center;
  cursor: pointer;
`;

const StyledInput = styled.input`
  /* These styles are to hide the circular state indicator of the radio input, as per design */
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const CustomRadioCard: React.FC<Props> = ({ checked, name, value, handleChange, icon, labelId }) => {
  return (
    <Root checked={checked}>
      <Label checked={checked}>
        {/* Use custom input so that the radio control can be hidden */}
        <StyledInput type="radio" checked={checked} name={name} onChange={handleChange} value={value} />
        {icon}
        <T.Body2>
          <FormattedMessage id={labelId} />
        </T.Body2>
      </Label>
    </Root>
  );
};

export default CustomRadioCard;
